import * as RadixPopover from '@radix-ui/react-popover';
import dynamic from 'next/dynamic';
import { type ComponentPropsWithoutRef, forwardRef, memo, type ReactElement, type ReactNode } from 'react';

import { ImageSearchIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { searchFieldChrome, searchFieldIconSlot } from '~/components/Search/searchFieldChrome';
import {
  SearchCommandFilterCount,
  type SearchCommandFilterCountProps,
} from '~/components/SearchCommand/SearchCommandFilterCount';
import { SearchCommandModeSelector } from '~/components/SearchCommand/SearchCommandModeSelector';
import {
  SEARCH_COMMAND_VISUAL_SEARCH_TOOLTIP,
  SearchCommandVisualSearchButton,
} from '~/components/SearchCommand/SearchCommandVisualSearchButton';
import { SmartSearchImageSearchStatus } from '~/components/SearchCommand/SmartSearchImageSearchStatus/SmartSearchImageSearchStatus';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';

export type SearchCommandOpenButton = ReactElement<ComponentPropsWithoutRef<'button'>>;

export const wrapSearchCommandOpenButton = (button: SearchCommandOpenButton) => (
  <RadixPopover.Trigger asChild>{button}</RadixPopover.Trigger>
);

const renderSearchCommandOpenButton = (button: SearchCommandOpenButton) => button;

export type SearchCommandTriggerProps = ComponentPropsWithoutRef<'button'> &
  Partial<Pick<SearchCommandFilterCountProps, 'count'>> & {
    compact?: boolean;
    'data-testid'?: string;
    onVisualSearchClick?: () => void;
    placeholder: string | undefined;
    search?: string;
    visualSearchTooltip?: string;
    wrapOpenButton?: (button: SearchCommandOpenButton) => ReactNode;
  };

export const _SearchCommandTrigger = forwardRef<HTMLDivElement, SearchCommandTriggerProps>(
  (
    {
      children,
      className,
      compact,
      count,
      'data-testid': dataTestId = 'SEARCH_COMMAND_TRIGGER',
      onVisualSearchClick,
      placeholder,
      search,
      visualSearchTooltip = SEARCH_COMMAND_VISUAL_SEARCH_TOOLTIP,
      onClick,
      wrapOpenButton = renderSearchCommandOpenButton,
      ...restOfProps
    },
    ref,
  ) => {
    const { canUseSmartSearch } = usePrivateSearchContext();
    const { isActiveImageSearch, similarToImageSearchAvailable } = usePrivateSearchUIHelpers();
    const { filtersCanBeSaved } = useAppliedFiltersCount();
    const showImageSearch = similarToImageSearchAvailable && !search && !isActiveImageSearch && !filtersCanBeSaved;
    const showSimilarToStatus = isActiveImageSearch && !search;
    const showModeSelector = !showSimilarToStatus;
    const isModeSelectorInteractive = showModeSelector && canUseSmartSearch;
    const showVisualSearchButton = showImageSearch && !!onVisualSearchClick && !count;
    const hasTrailingEndActions = !!children || showVisualSearchButton;
    const handleOpenSearch = isActiveImageSearch ? undefined : onClick;
    const openButton = (
      <button
        aria-label={search || placeholder}
        className={cn(
          'order-2 flex h-full min-h-0 min-w-0 flex-1 items-center self-stretch overflow-hidden bg-transparent text-left [outline:none] focus-visible:ring-0 focus-visible:[outline:none]',
          isModeSelectorInteractive ? 'ui2026:py-1 pr-3 ui2026:pr-1' : 'px-3 ui2026:px-1 ui2026:py-1',
          isActiveImageSearch ? 'gap-1' : 'gap-2 ui2026:gap-1',
          showSimilarToStatus && 'ui2026:pl-1',
        )}
        data-testid="SEARCH_COMMAND_OPEN_BUTTON"
        data-value={search}
        {...restOfProps}
        onClick={handleOpenSearch}
        type="button"
      >
        {showModeSelector && !isModeSelectorInteractive ? (
          <span className="flex shrink-0 items-center" data-testid="SEARCH_COMMAND_LEADING_SLOT">
            <SearchCommandModeSelector search={search} />
          </span>
        ) : null}
        <span
          className={cn(
            'flex min-w-0 flex-1 items-center justify-start overflow-hidden text-left font-medium ui2026:font-normal',
            isActiveImageSearch && 'ui2026:self-stretch',
            compact ? 'text-14' : 'text-16 ui2026:text-14',
            search ? 'ads:text-primary text-gray-12' : 'ads:text-placeholder text-gray-9 ui2026:text-gray-10',
          )}
        >
          {search && (
            <span className="min-w-0 flex-1 truncate text-left" data-testid="SEARCH_COMMAND_TRIGGER_VALUE">
              {search}
            </span>
          )}
          {!search && isActiveImageSearch && <SmartSearchImageSearchStatus />}
          {!search && !isActiveImageSearch && (
            <span className="truncate" data-testid="SEARCH_COMMAND_TRIGGER_PLACEHOLDER">
              {placeholder}
            </span>
          )}
        </span>
        {showImageSearch && !onVisualSearchClick && (
          <span className={cn('ml-auto', searchFieldIconSlot())} data-testid="SEARCH_COMMAND_TRAILING_SLOT">
            <ImageSearchIcon
              className="shrink-0 ads:text-icon-default text-gray-10"
              data-testid="SEARCH_COMMAND_IMAGE_SEARCH_ICON"
              size="sm"
            />
          </span>
        )}
        {!!count && <SearchCommandFilterCount count={count} />}
      </button>
    );

    return (
      <div
        className={cn(
          searchFieldChrome({ compact, padded: false }),
          'cursor-pointer items-stretch ads:has-[[data-testid=SEARCH_COMMAND_OPEN_BUTTON]:focus-visible]:ring-focus has-[[data-testid=SEARCH_COMMAND_OPEN_BUTTON]:focus-visible]:ring-blue-9',
          showModeSelector && isModeSelectorInteractive && 'pl-2 ui2026:pl-1',
          className,
          hasTrailingEndActions && 'pr-1',
        )}
        data-testid={dataTestId}
        id="SEARCH_COMMAND_TRIGGER"
        ref={ref}
      >
        {isModeSelectorInteractive ? (
          <span className="order-1 flex shrink-0 items-center self-center" data-testid="SEARCH_COMMAND_LEADING_SLOT">
            <SearchCommandModeSelector search={search} tabIndex={-1} />
          </span>
        ) : null}
        {wrapOpenButton(openButton)}
        {children ? <span className="order-3 flex shrink-0 items-center gap-2 ui2026:gap-1">{children}</span> : null}
        {showVisualSearchButton && onVisualSearchClick ? (
          <span className="order-4 ml-auto flex shrink-0 items-center" data-testid="SEARCH_COMMAND_TRAILING_SLOT">
            <SearchCommandVisualSearchButton onClick={onVisualSearchClick} tooltip={visualSearchTooltip} />
          </span>
        ) : null}
      </div>
    );
  },
);

_SearchCommandTrigger.displayName = '_SearchCommandTrigger';

export const SearchCommandTrigger = memo(_SearchCommandTrigger);

SearchCommandTrigger.displayName = 'SearchCommandTrigger';

const SearchCommandPopoverTriggerShortcut = dynamic(
  () =>
    import('~/components/SearchCommand/SearchCommandTriggerShortcut').then((mod) => mod.SearchCommandTriggerShortcut),
  { ssr: false },
);

export type SearchCommandPopoverTriggerProps = ComponentPropsWithoutRef<'button'> &
  Partial<Pick<SearchCommandFilterCountProps, 'count'>> & {
    compact?: boolean;
    placeholder: string | undefined;
    search?: string;
  };

/** Develop-style trigger button for `MobileSearchCommand` (Radix popover `asChild`). */
export const _SearchCommandPopoverTrigger = forwardRef<HTMLButtonElement, SearchCommandPopoverTriggerProps>(
  ({ className, compact, count, placeholder, search, onClick, ...restOfProps }, ref) => {
    const { canUseSmartSearch } = usePrivateSearchContext();
    const { isActiveImageSearch, similarToImageSearchAvailable } = usePrivateSearchUIHelpers();
    const { filtersCanBeSaved } = useAppliedFiltersCount();
    const showImageSearch = similarToImageSearchAvailable && !search && !isActiveImageSearch && !filtersCanBeSaved;
    const showShortcut = !search && !isActiveImageSearch && !count;

    return (
      <button
        className={cn(
          searchFieldChrome({ compact }),
          className,
          canUseSmartSearch && 'pr-3 pl-2',
          isActiveImageSearch && 'gap-1',
        )}
        data-testid="SEARCH_COMMAND_TRIGGER"
        data-value={search}
        id="SEARCH_COMMAND_TRIGGER"
        onClick={isActiveImageSearch ? undefined : onClick}
        ref={ref}
        type="button"
        {...restOfProps}
      >
        <SearchCommandModeSelector search={search} />
        <span
          className={cn(
            'flex min-w-0 flex-1 items-center font-medium ui2026:font-normal',
            compact ? 'text-14' : 'text-16 ui2026:text-14',
            search ? 'ads:text-primary text-gray-12' : 'ads:text-placeholder text-gray-9 ui2026:text-gray-10',
          )}
        >
          {search && <span data-testid="SEARCH_COMMAND_TRIGGER_VALUE">{search}</span>}
          {!search && isActiveImageSearch && <SmartSearchImageSearchStatus />}
          {!search && !isActiveImageSearch && (
            <div className="truncate" data-testid="SEARCH_COMMAND_TRIGGER_PLACEHOLDER">
              {placeholder}
            </div>
          )}
        </span>
        {showImageSearch && (
          <ImageSearchIcon className="ml-auto shrink-0 ads:text-icon-default text-gray-10" size="sm" />
        )}
        {showShortcut && <SearchCommandPopoverTriggerShortcut showImageSearch={showImageSearch} />}
        {!!count && <SearchCommandFilterCount count={count} />}
      </button>
    );
  },
);

_SearchCommandPopoverTrigger.displayName = '_SearchCommandPopoverTrigger';

export const SearchCommandPopoverTrigger = memo(_SearchCommandPopoverTrigger);

SearchCommandPopoverTrigger.displayName = 'SearchCommandPopoverTrigger';
